export function formatNumber(value: number, maximumFractionDigits = 0) {
  return new Intl.NumberFormat('fr-FR', {
    maximumFractionDigits,
  }).format(value);
}

export interface DistanceFormat {
  value: string;
  unit: 'm' | 'km';
}

/**
 * Formats a distance in meters into a human-readable format.
 *
 * @param meters - The distance in meters to format
 * @returns An object containing the formatted value and unit
 *
 * @example
 * ```typescript
 * formatDistance(500)    // { value: "500", unit: "m" }
 * formatDistance(1500)   // { value: "1.5", unit: "km" }
 * formatDistance(2345.6) // { value: "2.3", unit: "km" }
 * ```
 */
/**
 * Converts yearly kWh demand to estimated charging sessions per day.
 * - Divide by 365 to get daily kWh
 * - Divide by 32 kWh (average energy per charging session) to get sessions
 *
 * @param kWhPerYear - The energy demand in kWh per year
 * @returns The estimated number of charging sessions per day
 *
 * @example
 * ```typescript
 * convertYearlyKWhToSessionsPerDay(11680) // 1 session/day
 * ```
 */
export function convertYearlyKWhToSessionsPerDay(kWhPerYear: number): number {
  const KWH_PER_SESSION = 32;
  const DAYS_PER_YEAR = 365;
  return kWhPerYear / (KWH_PER_SESSION * DAYS_PER_YEAR);
}

export function formatDistance(meters: number): DistanceFormat {
  const isUnderKm = meters < 1000;
  const value = isUnderKm
    ? meters.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : (meters / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 });
  const unit = isUnderKm ? 'm' : 'km';
  return { value, unit };
}

/**
 * Format duration from seconds to human-readable string
 *
 * @param seconds - Duration in seconds
 * @param options - Formatting options
 * @param options.round - If true, rounds to nearest minute/hour (default: false)
 * @param options.detailed - If true, includes seconds in output (default: false)
 * @returns Formatted duration string (e.g., "5min", "1h 30min", "3min 43sec")
 */
export function formatDuration(
  seconds: number,
  options: { round?: boolean; detailed?: boolean } = {},
): string {
  const { round = false, detailed = false } = options;
  const hours = Math.floor(seconds / 3600);
  const minutes = round ? Math.round((seconds % 3600) / 60) : Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  if (hours > 0) {
    if (round) {
      return `${minutes >= 30 ? hours + 1 : hours}h`;
    }
    if (detailed) {
      return `${hours}h ${minutes}min ${secs}sec`;
    }
    return `${hours}h ${minutes}min`;
  }

  if (minutes > 0) {
    if (detailed) {
      return `${minutes}min ${secs}sec`;
    }
    return `${minutes}min`;
  }

  return `${secs}sec`;
}

/**
 * Format a number in compact notation (e.g., 1.2k, 500k, 1.2M)
 *
 * @param value - The number to format
 * @param maximumFractionDigits - Maximum decimal places (default: 1)
 * @returns Formatted compact string
 *
 * @example
 * ```typescript
 * formatCompactNumber(500)      // "500"
 * formatCompactNumber(1500)     // "1.5k"
 * formatCompactNumber(500000)   // "500k"
 * formatCompactNumber(1200000)  // "1.2M"
 * ```
 */
export function formatCompactNumber(value: number, maximumFractionDigits = 1): string {
  const absValue = Math.abs(value);

  if (absValue >= 1_000_000) {
    return `${(value / 1_000_000).toLocaleString(undefined, { maximumFractionDigits })}M`;
  }
  if (absValue >= 1_000) {
    return `${(value / 1_000).toLocaleString(undefined, { maximumFractionDigits })}k`;
  }
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
